import React from 'react';
import { Shield, Lock, Eye, FileText, Mail, Globe, CheckCircle } from 'lucide-react';

export default function PrivacyPolicy() {
  const lastUpdated = "November 27, 2025";

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="bg-blue-600 p-1.5 rounded-lg">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">OilWellX</span>
          </div>
          <a 
            href="/"
            className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors"
          >
            Back to App
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight sm:text-5xl mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            We are committed to protecting your data and ensuring the security of your oil field operations.
          </p>
          <div className="mt-4 inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-medium">
            Last Updated: {lastUpdated}
          </div>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-8 sm:p-12 space-y-10">
            
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Globe className="h-6 w-6 text-blue-600 mr-2" />
                Introduction
              </h2>
              <p className="text-gray-600 leading-relaxed">
                OilWellX ("we," "our," or "us") is a registered software application developed by <strong>TGE LLC</strong>. 
                We respect your privacy and are committed to protecting it through our compliance with this policy. 
                This policy describes the types of information we may collect from you or that you may provide when you visit 
                our application and our practices for collecting, using, maintaining, protecting, and disclosing that information.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Eye className="h-6 w-6 text-blue-600 mr-2" />
                Information We Collect
              </h2>
              <p className="text-gray-600 mb-4">
                We collect several types of information from and about users of our application, including:
              </p>
              <ul className="space-y-3">
                {[
                  "Personal identifiers such as name, email address, and phone number.",
                  "Operational data including well production figures, maintenance records, and equipment status.",
                  "Geolocation data for route optimization and field worker safety.",
                  "Device information such as IP address, browser type, and operating system.",
                  "Voice commands and audio data processed locally on your device for the X-Assist feature."
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* How We Use Your Information */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <FileText className="h-6 w-6 text-blue-600 mr-2" />
                How We Use Your Information
              </h2>
              <p className="text-gray-600 mb-4">
                We use information that we collect about you or that you provide to us, including any personal information:
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  "To present our application and its contents to you.",
                  "To provide you with information, products, or services that you request from us.",
                  "To fulfill any other purpose for which you provide it.",
                  "To carry out our obligations and enforce our rights arising from any contracts entered into between you and us.",
                  "To notify you about changes to our application or any products or services we offer or provide though it.",
                  "To improve our application and deliver a better and more personalized service."
                ].map((item, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-100 text-gray-700 text-sm">
                    {item}
                  </div>
                ))}
              </div>
            </section>

            {/* Third-Party Data Sharing */}
            <section className="bg-green-50 rounded-xl p-6 border border-green-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Shield className="h-6 w-6 text-green-600 mr-2" />
                We Never Share Your Data with Third Parties
              </h2>
              <p className="text-gray-700 leading-relaxed font-medium">
                <strong>Your data is yours alone.</strong> We do not sell, rent, trade, or otherwise share your personal information, 
                operational data, production records, or any other data you provide to OilWellX with third parties for marketing, 
                advertising, or any other commercial purposes.
              </p>
              <div className="mt-4 space-y-2">
                {[
                  "No data brokers or aggregators",
                  "No advertising networks",
                  "No analytics partners (except Google Analytics for our own use)",
                  "No social media companies",
                  "Complete multi-tenant isolation - your competitors never see your data"
                ].map((item, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-gray-600 text-sm mt-4">
                We only share data when legally required by court order or to protect safety/security, 
                or with service providers who help us operate the platform (e.g., cloud hosting, payment processing) 
                under strict confidentiality agreements.
              </p>
            </section>

            {/* SMS and Email Consent */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Mail className="h-6 w-6 text-blue-600 mr-2" />
                Communications Consent
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Email Communications</h3>
                  <p className="text-gray-600 mb-3">
                    By creating an account and using OilWellX, you consent to receive emails from us, including:
                  </p>
                  <ul className="space-y-2 ml-6">
                    {[
                      "Account notifications and security alerts",
                      "Service updates and new feature announcements",
                      "Billing and payment confirmations",
                      "System maintenance notifications",
                      "Alert notifications for well operations",
                      "Vendor invitation and coordination messages"
                    ].map((item, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-gray-600 mt-3">
                    You may opt out of non-essential emails via your account settings, but critical service-related 
                    communications cannot be disabled.
                  </p>
                </div>

                <div className="bg-blue-50 rounded-lg p-5 border border-blue-200">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">SMS Text Messaging Consent</h3>
                  <p className="text-gray-700 mb-3">
                    By providing your mobile phone number and opting in to SMS notifications, you expressly consent 
                    to receive automated SMS text messages from OilWellX at the phone number you provide, including:
                  </p>
                  <ul className="space-y-2 ml-6 mb-4">
                    {[
                      "Well alert notifications (downtime, pressure anomalies, production issues)",
                      "Service ticket updates and vendor coordination",
                      "Vendor invitation and onboarding messages",
                      "Time-sensitive operational alerts",
                      "Two-factor authentication codes",
                      "Service reminders and scheduled maintenance notifications"
                    ].map((item, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="bg-white rounded-lg p-4 space-y-2 text-sm text-gray-600">
                    <p><strong>Message Frequency:</strong> Varies based on your operations and alert settings. You may receive multiple messages per day during critical events.</p>
                    <p><strong>Message & Data Rates:</strong> Standard message and data rates may apply from your carrier.</p>
                    <p><strong>Opt-Out:</strong> Reply <strong>STOP</strong> to any message to unsubscribe from SMS alerts. You will receive a confirmation message.</p>
                    <p><strong>Help:</strong> Reply <strong>HELP</strong> for assistance or contact <a href="mailto:support@oilwellx.com" className="text-blue-600 hover:underline">support@oilwellx.com</a></p>
                    <p><strong>Carrier Support:</strong> Supported carriers include AT&T, Verizon, T-Mobile, Sprint, and other major US carriers.</p>
                  </div>
                  <p className="text-gray-600 text-sm mt-3">
                    By consenting to SMS communications, you certify that you are authorized to receive text messages 
                    at the number provided and that you are at least 18 years old.
                  </p>
                </div>
              </div>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Lock className="h-6 w-6 text-blue-600 mr-2" />
                Data Security
              </h2>
              <p className="text-gray-600 leading-relaxed">
                We have implemented measures designed to secure your personal information from accidental loss and from unauthorized access, use, alteration, and disclosure. 
                All information you provide to us is stored on our secure servers behind firewalls using Google Cloud Platform and Firebase infrastructure. 
                Any payment transactions are encrypted using SSL/TLS technology and processed through PCI-compliant payment processors.
                <br /><br />
                The safety and security of your information also depends on you. Where we have given you (or where you have chosen) a password for access to certain parts of our application, 
                you are responsible for keeping this password confidential. We ask you not to share your password with anyone. We strongly recommend enabling two-factor authentication (2FA) for additional security.
              </p>
            </section>

            {/* Contact Information */}
            <section className="bg-blue-50 rounded-xl p-6 border border-blue-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Mail className="h-6 w-6 text-blue-600 mr-2" />
                Contact Information
              </h2>
              <p className="text-gray-700 mb-4">
                To ask questions or comment about this privacy policy and our privacy practices, contact us at:
              </p>
              <div className="space-y-3">
                <div>
                  <p className="font-semibold text-blue-900">TGE LLC (Oil & Gas Services)</p>
                  <p className="text-gray-600">Los Angeles, California</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Email:</p>
                  <a href="mailto:privacy@oilwellx.com" className="text-blue-600 hover:text-blue-800 font-medium">
                    privacy@oilwellx.com
                  </a>
                  <br />
                  <a href="mailto:support@oilwellx.com" className="text-blue-600 hover:text-blue-800 font-medium">
                    support@oilwellx.com
                  </a>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">SMS Support:</p>
                  <p className="text-gray-600">Reply HELP to any message or text <strong>+1 (855) 301-3570</strong></p>
                </div>
              </div>
            </section>

            {/* Footer Copyright */}
            <div className="pt-8 border-t border-gray-200 text-center text-sm text-gray-500">
              <p>&copy; 2025-2026 TGE LLC. All rights reserved.</p>
              <p className="mt-1">OilWellX is a registered software application of TGE LLC.</p>
              <div className="mt-3 space-x-4">
                <a href="/terms" className="text-blue-600 hover:underline">Terms of Service</a>
                <a href="/vendor/register" className="text-blue-600 hover:underline">Vendor Registration</a>
              </div>
            </div>

          </div>
        </div>
      </main>
    </div>
  );
}
